import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';

import { selectTransactionJob } from '../../transactions/transaction-job.selectors';
import {
    blueSageRefundLegOfAttempt,
    maskLast4,
    toMajorUnits,
    toMinorUnits,
} from '../bluesage.util';

import { BlueSageRefundDialogActions } from './bluesage-refund-dialog.actions';
import { blueSageRefundDialogFeature } from './bluesage-refund-dialog.feature';
import {
    BLUESAGE_PROVIDER,
    BLUESAGE_REFUND_REASONS,
    REFUND_REASON_NEEDING_DETAIL,
    STANDALONE_CHANNEL,
    isRefundReasonComplete,
} from './bluesage-refund-dialog.state';
import { describeBlueSageRefundOutcome, refundToCardMinor } from './bluesage-refund-dialog.util';

/**
 * The refund dialog.
 *
 * The interaction, decided with YMM 2026-08-27:
 *
 *  1. Operator picks **Refund** from the 3-dot menu on a transaction row.
 *  2. The dialog opens with the amount **pre-filled to the full refundable
 *     amount** — the common case is one click away, no typing.
 *  3. If they need less, they edit it. Anything up to `refundable_amount` is
 *     accepted.
 *  4. The confirm carries the figure: *Refund $1,234.00 to ••••4242*.
 *
 * **No terminal messaging anywhere.** Measured on production with the device
 * off and on: a full refund works either way. Gating a refund on terminal state
 * would be inventing a requirement that does not exist.
 */
@Component({
    selector: 'app-bluesage-refund-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        DialogModule,
        InputNumberModule,
        InputTextModule,
        MessagesModule,
    ],
    providers: [ CurrencyPipe ],
    templateUrl: './bluesage-refund-dialog.component.html',
    styleUrls: [ './bluesage-refund-dialog.component.scss' ],
})
export class BlueSageRefundDialogComponent {

    actions = BlueSageRefundDialogActions;

    viewModel$ = this.store.select(createSelector({
        open: blueSageRefundDialogFeature.selectOpen,
        submitting: blueSageRefundDialogFeature.selectSubmitting,
        done: blueSageRefundDialogFeature.selectDone,
        error: blueSageRefundDialogFeature.selectError,
        reasonCategory: blueSageRefundDialogFeature.selectReasonCategory,
        reasonDetail: blueSageRefundDialogFeature.selectReasonDetail,

        /** Only `Other` cannot stand on its own. */
        detailRequired: createSelector(
            blueSageRefundDialogFeature.selectReasonCategory,
            (category) => category === REFUND_REASON_NEEDING_DETAIL,
        ),

        /** Major units for the input. Money crosses the boundary exactly once. */
        amount: createSelector(blueSageRefundDialogFeature.selectAmountMinor, toMajorUnits),
        maxAmount: createSelector(blueSageRefundDialogFeature.selectRefundableMinor, toMajorUnits),

        /**
         * What the customer actually gets back — the mirror of the card price in
         * Take Payment. The operator types what comes off the JOB; this is the
         * number they say out loud, and the two are not the same on a
         * dual-pricing location.
         *
         * **Both paths are quoted, because they differ and the difference is the
         * surprising part.**
         *
         *  - `full` — the whole capture reverses, tip and tax included. That is
         *    the MOST money any refund returns and it was the one case with no
         *    figure on screen at all.
         *  - `partial` — base plus the differential that base was charged. The
         *    tip stays with the crew.
         *
         * The full-refund test mirrors the server's literally
         * (`refundMinor === fullMinor` in `transactions.mutations.ts`): it is
         * the ORIGINAL that decides, never the remaining, so the last slice of a
         * part-refunded sale is still a partial and is still grossed up.
         *
         * `undefined` when there is nothing worth saying — no amount typed, or a
         * sale where the card was charged exactly the base.
         */
        cardRefund: createSelector(
            blueSageRefundDialogFeature.selectAmountMinor,
            blueSageRefundDialogFeature.selectOriginalMinor,
            blueSageRefundDialogFeature.selectRefundableMinor,
            blueSageRefundDialogFeature.selectSaleDifferentialMinor,
            blueSageRefundDialogFeature.selectSalePaidMinor,
            blueSageRefundDialogFeature.selectSaleTipMinor,
            blueSageRefundDialogFeature.selectRefundTip,
            (
                amountMinor, originalMinor, refundableMinor, differentialMinor,
                paidMinor, tipMinor, refundTip,
            ) => {
                if (amountMinor <= 0) { return undefined; }

                // ⚠ A full refund's figure DEPENDS ON THE TIP CHOICE, and quoting
                // the whole capture regardless made the dialog contradict itself:
                // it read "the customer gets $1.18 back … including any tip"
                // directly above an unticked "also refund the $0.15 tip". Found by
                // YMM on 2026-08-29.
                if (amountMinor >= originalMinor) {
                    // Tip returned: no amount is sent and the rail reverses
                    // everything.
                    if (refundTip || tipMinor <= 0) {
                        return paidMinor > originalMinor
                            ? { full: true, toCard: toMajorUnits(paidMinor) }
                            : undefined;
                    }

                    // Tip withheld: base and its differential only, which is the
                    // same figure the partial path computes for the whole base.
                    const withoutTipMinor = refundToCardMinor(
                        amountMinor, 0, originalMinor, differentialMinor, paidMinor,
                    );

                    return {
                        full: false,
                        toCard: toMajorUnits(withoutTipMinor),
                        differential: toMajorUnits(withoutTipMinor - amountMinor),
                    };
                }

                if (differentialMinor <= 0) { return undefined; }

                const alreadyRefundedMinor = Math.max(0, originalMinor - refundableMinor);
                const toCardMinor = refundToCardMinor(
                    amountMinor, alreadyRefundedMinor, originalMinor, differentialMinor,
                    paidMinor,
                );

                return {
                    full: false,
                    toCard: toMajorUnits(toCardMinor),
                    differential: toMajorUnits(toCardMinor - amountMinor),
                };
            },
        ),

        /**
         * The "also refund the tip" choice — offered ONLY on a full refund of a
         * sale that carried a tip, because that is the only time there is
         * anything to withhold. Anywhere else the question is noise.
         */
        tipChoice: createSelector(
            blueSageRefundDialogFeature.selectAmountMinor,
            blueSageRefundDialogFeature.selectOriginalMinor,
            blueSageRefundDialogFeature.selectSaleTipMinor,
            blueSageRefundDialogFeature.selectRefundTip,
            (amountMinor, originalMinor, tipMinor, refundTip) =>
                amountMinor >= originalMinor && tipMinor > 0
                    ? { tip: toMajorUnits(tipMinor), refundTip }
                    : undefined,
        ),

        card: createSelector(
            blueSageRefundDialogFeature.selectCardLast4,
            (last4) => last4 ? maskLast4(last4) : 'the card',
        ),

        /**
         * A charge run ON the device cannot take a partial at any amount.
         *
         * Requirement: default it to a full refund and SAY WHY — but do not hard
         * block the attempt. The middleware deliberately does not pre-empt the
         * processor, so if Valor ever enables this the path simply starts
         * working; blocking client-side would hide that.
         */
        standaloneOnly: createSelector(
            blueSageRefundDialogFeature.selectChannel,
            blueSageRefundDialogFeature.selectProvider,
            // Only meaningful on OUR rail. A cash transaction has no channel and
            // no processor to refuse a partial.
            (channel, provider) => provider === BLUESAGE_PROVIDER && channel === STANDALONE_CHANNEL,
        ),

        /**
         * Whether this refund will actually move money.
         *
         * A transaction taken on another system — or in cash — has no BlueSage
         * reference to refund against, so this records a refund on the job and
         * nothing reaches a card. Saying "Refunded to ••••4242" there would tell
         * the operator money went back that never left, and the person owed it
         * would still be waiting.
         */
        movesMoney: createSelector(
            blueSageRefundDialogFeature.selectProvider,
            (provider) => provider === BLUESAGE_PROVIDER,
        ),

        /** The rail refused a partial and named the full figure — one click out. */
        fullRefundInstead: createSelector(
            blueSageRefundDialogFeature.selectFullRefundOnlyMinor,
            (minor) => minor === undefined ? undefined : toMajorUnits(minor),
        ),

        /**
         * Nothing to refund. The row should not have offered Refund at all, but a
         * dialog that lets someone submit zero is worse than one that says so.
         */
        nothingLeft: createSelector(
            blueSageRefundDialogFeature.selectRefundableMinor,
            (minor) => minor <= 0,
        ),

        // Requirement: a BlueSage refund answers as soon as its leg exists and
        // settles in the background, so once sent this reports what the LEG says
        // — waiting, confirmed, or not — and never what the request implied.
        // Read live from the job: the leg arrives over the job subscription or
        // the settle poll, and the dialog must move with it.
        outcome: createSelector(
            blueSageRefundDialogFeature.selectDone,
            blueSageRefundDialogFeature.selectAwaitsConfirmation,
            blueSageRefundDialogFeature.selectConfirmationOverdue,
            blueSageRefundDialogFeature.selectTransactionId,
            blueSageRefundDialogFeature.selectKnownLegIds,
            selectTransactionJob,
            (done, awaitsConfirmation, overdue, transactionId, knownLegIds, job) =>
                describeBlueSageRefundOutcome({
                    done,
                    awaitsConfirmation,
                    overdue,
                    leg: blueSageRefundLegOfAttempt(job?.transactions, transactionId, knownLegIds),
                }),
        ),

        canConfirm: createSelector(
            blueSageRefundDialogFeature.selectAmountMinor,
            blueSageRefundDialogFeature.selectRefundableMinor,
            blueSageRefundDialogFeature.selectSubmitting,
            blueSageRefundDialogFeature.selectReasonCategory,
            blueSageRefundDialogFeature.selectReasonDetail,
            // A reason is now REQUIRED. It is the only record of why a customer
            // was given money back, and a box nobody has to fill in produces a
            // refund ledger of blanks.
            (amountMinor, refundableMinor, submitting, category, detail) =>
                !submitting
                && amountMinor > 0
                && amountMinor <= refundableMinor
                && isRefundReasonComplete(category, detail),
        ),
    }));

    constructor(
        public store: Store,
    ) {}

    /**
     * Major units in, minor units out.
     *
     * A component method purely to preprocess the payload, which is the
     * documented exception to dispatching straight from the template — amounts
     * are integer cents end to end and the input speaks dollars.
     */
    toMinor = toMinorUnits;

    /** The fixed list, as YMM defined it. Not a component-local invention. */
    reasons = BLUESAGE_REFUND_REASONS;
}
